import { CONSENT_FIELDS, placeholderOf } from "./consentFields";

export const CONSENT_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
];

export const DEFAULT_LANGUAGE = "en";

/** Styling every element starts with; the Inspector edits these keys. */
export const DEFAULT_STYLE = {
  fontFamily: "Helvetica",
  fontSize: 11,
  fontWeight: "normal",
  fontStyle: "normal",
  align: "left",
  lineHeight: 1.5,
  letterSpacing: 0,
  color: "#1A1A1A",
  background: "transparent",
  border: "none",
  paddingY: 2,
  paddingX: 0,
  marginTop: 0,
  marginBottom: 6,
  width: 100,
};

export const FONT_FAMILIES = ["Helvetica", "Times", "Courier"];
export const FONT_WEIGHTS = ["normal", "bold"];
export const TEXT_ALIGNMENTS = ["left", "center", "right", "justify"];
export const BORDER_STYLES = ["none", "solid", "dashed", "dotted", "box"];

const text = (en = "", hi = "") => ({ en, hi });

export const ELEMENT_DEFS = {
  // ---------------------------------------------------------------- Basic
  heading: {
    label: "Heading",
    group: "basic",
    hint: "Document title",
    localizedPaths: ["content"],
    make: () => ({
      content: text("CONSENT FORM"),
      style: { fontSize: 16, fontWeight: "bold", align: "center", marginBottom: 10 },
    }),
  },
  subHeading: {
    label: "Sub Heading",
    group: "basic",
    hint: "Section title",
    localizedPaths: ["content"],
    make: () => ({
      content: text("Section"),
      style: { fontSize: 13, fontWeight: "bold", marginTop: 6 },
    }),
  },
  paragraph: {
    label: "Paragraph",
    group: "basic",
    hint: "Body text, placeholders allowed",
    localizedPaths: ["content"],
    make: () => ({ content: text("Type the consent text here.") }),
  },
  richText: {
    label: "Rich Text",
    group: "basic",
    hint: "Multi-line block: paragraphs or bullets",
    localizedPaths: ["content"],
    make: () => ({
      content: text("Line one\nLine two"),
      props: { listStyle: "none" },
    }),
  },
  divider: {
    label: "Divider",
    group: "basic",
    hint: "Horizontal rule",
    make: () => ({
      props: { thickness: 1, color: "#9E9E9E" },
      style: { marginTop: 6, marginBottom: 6 },
    }),
  },
  spacer: {
    label: "Spacer",
    group: "basic",
    hint: "Vertical gap",
    make: () => ({ props: { height: 12 } }),
  },
  pageBreak: {
    label: "Page Break",
    group: "basic",
    hint: "Continue on the next page",
    make: () => ({}),
  },

  clinicInfo: {
    label: "Clinic Header",
    group: "clinical",
    hint: "Letterhead: logo, clinic and treating doctor",
    localizedPaths: ["content"],
    fieldList: true,
    make: () => ({
      content: text(""),
      props: {
        fields: [
          "clinic.name",
          "clinic.address",
          "clinic.phone",
          "clinic.email",
          "clinic.timings",
          "doctor.name",
          "doctor.qualification",
          "doctor.speciality",
          "doctor.department",
          "doctor.designation",
          "doctor.experience",
          "doctor.registrationNo",
        ],
        showLogo: true,
        logoHeight: 18,
      },
    }),
  },
  patientInfo: {
    label: "Patient Information",
    group: "clinical",
    hint: "Name, UHID, age, gender, mobile",
    localizedPaths: ["content"],
    fieldList: true,
    make: () => ({
      content: text("Patient Details"),
      props: {
        fields: [
          "patient.name",
          "patient.uhid",
          "patient.age",
          "patient.gender",
          "patient.mobile",
        ],
        columns: 2,
        showHeading: true,
      },
    }),
  },
  appointmentInfo: {
    label: "Appointment Information",
    group: "clinical",
    hint: "Date and time",
    localizedPaths: ["content"],
    fieldList: true,
    make: () => ({
      content: text("Appointment"),
      props: {
        fields: ["appointment.date", "appointment.time"],
        columns: 2,
        showHeading: true,
      },
    }),
  },
  doctorInfo: {
    label: "Doctor Information",
    group: "clinical",
    hint: "Treating doctor",
    localizedPaths: ["content"],
    fieldList: true,
    make: () => ({
      content: text("Treating Doctor"),
      props: {
        fields: ["doctor.name", "doctor.registrationNo"],
        columns: 2,
        showHeading: true,
      },
    }),
  },
  diagnosis: {
    label: "Diagnosis",
    group: "clinical",
    dynamic: true,
    localizedPaths: ["label"],
    make: () => ({ label: text("Diagnosis"), field: "diagnosis" }),
  },
  treatment: {
    label: "Treatment",
    group: "clinical",
    dynamic: true,
    localizedPaths: ["label"],
    make: () => ({ label: text("Treatment advised"), field: "treatment" }),
  },
  procedure: {
    label: "Procedure",
    group: "clinical",
    dynamic: true,
    localizedPaths: ["label"],
    make: () => ({ label: text("Procedure"), field: "procedure" }),
  },
  toothInfo: {
    label: "Tooth Information",
    group: "clinical",
    dynamic: true,
    localizedPaths: ["label"],
    make: () => ({ label: text("Tooth (FDI)"), field: "tooth.number" }),
  },
  cost: {
    label: "Cost / Estimated Cost",
    group: "clinical",
    dynamic: true,
    localizedPaths: ["label"],
    make: () => ({
      label: text("Estimated cost"),
      field: "estimatedCost",
      style: { fontWeight: "bold" },
    }),
  },

  // -------------------------------------------------------- Form controls
  textField: {
    label: "Text Field",
    group: "controls",
    control: true,
    localizedPaths: ["label", "placeholder"],
    make: () => ({
      label: text("Label"),
      placeholder: text(""),
      props: { required: false, lines: 1 },
    }),
  },
  textArea: {
    label: "Text Area",
    group: "controls",
    control: true,
    localizedPaths: ["label", "placeholder"],
    make: () => ({
      label: text("Remarks"),
      placeholder: text(""),
      props: { required: false, lines: 3 },
    }),
  },
  checkbox: {
    label: "Checkbox",
    group: "controls",
    control: true,
    localizedPaths: ["label"],
    make: () => ({
      label: text("I understand and agree"),
      props: { required: true },
    }),
  },
  checkboxGroup: {
    label: "Checkbox Group",
    group: "controls",
    control: true,
    hasOptions: true,
    localizedPaths: ["label"],
    make: () => ({
      label: text("Select all that apply"),
      options: [
        { value: "option-1", label: text("Option one") },
        { value: "option-2", label: text("Option two") },
      ],
      props: { required: false },
    }),
  },
  radioGroup: {
    label: "Radio Group",
    group: "controls",
    control: true,
    hasOptions: true,
    localizedPaths: ["label"],
    make: () => ({
      label: text("Choose one"),
      options: [
        { value: "yes", label: text("Yes") },
        { value: "no", label: text("No") },
      ],
      props: { required: true, inline: true },
    }),
  },
  select: {
    label: "Select",
    group: "controls",
    control: true,
    hasOptions: true,
    localizedPaths: ["label"],
    make: () => ({
      label: text("Select"),
      options: [
        { value: "option-1", label: text("Option one") },
        { value: "option-2", label: text("Option two") },
      ],
      props: { required: false },
    }),
  },
  date: {
    label: "Date",
    group: "controls",
    control: true,
    localizedPaths: ["label"],
    make: () => ({
      label: text("Date"),
      props: { required: false, format: "DD-MM-YYYY" },
    }),
  },
  number: {
    label: "Number",
    group: "controls",
    control: true,
    localizedPaths: ["label"],
    make: () => ({ label: text("Number"), props: { required: false } }),
  },

  // ---------------------------------------------------------------- Media
  image: {
    label: "Image",
    group: "media",
    localizedPaths: ["content"],
    make: () => ({
      content: text("Image"),
      props: { url: "", height: 40, align: "left" },
    }),
  },
  clinicLogo: {
    label: "Clinic Logo",
    group: "media",
    hint: "Resolved from the clinic at print time",
    make: () => ({ props: { height: 20, align: "center" } }),
  },
  signature: {
    label: "Signature",
    group: "media",
    localizedPaths: ["label"],
    make: () => ({
      label: text("Signature"),
      props: { width: 60, showDate: false },
    }),
  },

  // -------------------------------------------------------------- Special
  consentDeclaration: {
    label: "Consent Declaration",
    group: "special",
    hint: "Declaration paragraph + agreement checkbox",
    localizedPaths: ["content", "label"],
    make: () => ({
      content: text(
        "I confirm that the procedure, its alternatives, risks and the estimated cost have been explained to me in a language I understand. I give my consent voluntarily."
      ),
      label: text("I have read and understood the above"),
      props: { requireAcknowledgement: true },
      style: { border: "box", paddingX: 6, paddingY: 6 },
    }),
  },
  signatureBlock: {
    label: "Signature Block",
    group: "special",
    hint: "Side-by-side signature lines",
    make: () => ({
      props: {
        signatories: [
          { key: "patient", label: text("Patient Signature"), showDate: true },
          { key: "doctor", label: text("Doctor Signature"), showDate: true },
        ],
      },
      style: { marginTop: 14 },
    }),
  },
  customField: {
    label: "Custom Field",
    group: "special",
    hint: "Label + value, filled at print time",
    control: true,
    localizedPaths: ["label"],
    make: () => ({
      label: text("Custom label"),
      props: { required: false, fallback: "" },
    }),
  },
  dynamicField: {
    label: "Dynamic Field",
    group: "special",
    hint: "Any registry field",
    dynamic: true,
    localizedPaths: ["label"],
    make: () => ({
      label: text("Patient name"),
      field: "patient.name",
      props: { inline: true, fallback: "" },
    }),
  },
};

export const ELEMENT_GROUPS = [
  { key: "basic", label: "Basic" },
  { key: "clinical", label: "Patient / Clinical Data" },
  { key: "controls", label: "Form Controls" },
  { key: "media", label: "Media" },
  { key: "special", label: "Special" },
];

export const PALETTE = ELEMENT_GROUPS.map((group) => ({
  ...group,
  items: Object.entries(ELEMENT_DEFS)
    .filter(([, def]) => def.group === group.key)
    .map(([type, def]) => ({ type, label: def.label, hint: def.hint || "" })),
}));

export const elementDef = (type) => ELEMENT_DEFS[type] || null;

export const elementTypeLabel = (type) => elementDef(type)?.label || type;

export const isKnownElementType = (type) => Boolean(ELEMENT_DEFS[type]);

export const localized = (value, lang = DEFAULT_LANGUAGE) => {
  if (value == null) return "";
  if (typeof value === "string") return value;

  const own = value[lang];
  if (typeof own === "string" && own.trim()) return own;

  for (const { code } of CONSENT_LANGUAGES) {
    const other = value[code];
    if (typeof other === "string" && other.trim()) return other;
  }
  return "";
};

export const rawLocalized = (value, lang = DEFAULT_LANGUAGE) => {
  if (value == null) return "";
  if (typeof value === "string") return lang === DEFAULT_LANGUAGE ? value : "";
  return typeof value[lang] === "string" ? value[lang] : "";
};

export const withLocalized = (value, lang, next) => {
  const base = typeof value === "string" ? { en: value, hi: "" } : { ...(value || {}) };
  return { ...base, [lang]: next };
};

// ------------------------------------------------------------------ design

export const emptyDesign = () => ({
  pages: [{ pageNumber: 1, elements: [] }],
  settings: {},
});

export const allElements = (design) =>
  (design?.pages || []).flatMap((page) => page.elements || []);

export const designHasContent = (design) => allElements(design).length > 0;

const renumber = (pages) =>
  pages.map((page, index) => ({ ...page, pageNumber: index + 1 }));

export const nextElementId = (design, type) => {
  const taken = new Set(allElements(design).map((element) => element.id));

  for (let index = 1; index < 10000; index += 1) {
    const candidate = `${type}-${index}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${type}-${Date.now()}`;
};

export const createElement = (type, design = emptyDesign()) => {
  const def = elementDef(type);
  if (!def) throw new Error(`Unknown consent element type: ${type}`);

  const { style, ...rest } = def.make();

  return {
    id: nextElementId(design, type),
    type,
    ...rest,
    style: { ...DEFAULT_STYLE, ...(style || {}) },
    layout: { pageBreakBefore: false, pageBreakAfter: false },
  };
};

export const findElement = (design, id) => {
  const pages = design?.pages || [];

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const elements = pages[pageIndex].elements || [];
    const index = elements.findIndex((element) => element.id === id);
    if (index > -1) {
      return { element: elements[index], pageIndex, index };
    }
  }
  return null;
};

const mapPage = (design, pageIndex, change) => ({
  ...design,
  pages: design.pages.map((page, index) =>
    index === pageIndex ? { ...page, elements: change(page.elements || []) } : page
  ),
});

export const addElement = (design, { type, pageNumber, afterId } = {}) => {
  const element = createElement(type, design);

  let pageIndex = Math.max(
    0,
    (pageNumber ? pageNumber - 1 : design.pages.length - 1) || 0
  );
  let at = (design.pages[pageIndex]?.elements || []).length;

  if (afterId) {
    const found = findElement(design, afterId);
    if (found) {
      pageIndex = found.pageIndex;
      at = found.index + 1;
    }
  }

  return {
    design: mapPage(design, pageIndex, (elements) => [
      ...elements.slice(0, at),
      element,
      ...elements.slice(at),
    ]),
    element,
  };
};

/** Shallow-merges `style`, `props` and `layout`; replaces anything else. */
export const updateElement = (design, id, patch = {}) => {
  const found = findElement(design, id);
  if (!found) return design;

  return mapPage(design, found.pageIndex, (elements) =>
    elements.map((element) =>
      element.id === id
        ? {
            ...element,
            ...patch,
            style: { ...element.style, ...(patch.style || {}) },
            props: { ...element.props, ...(patch.props || {}) },
            layout: { ...element.layout, ...(patch.layout || {}) },
          }
        : element
    )
  );
};

export const setElementText = (design, id, path, lang, value) => {
  const found = findElement(design, id);
  if (!found) return design;

  return updateElement(design, id, {
    [path]: withLocalized(found.element[path], lang, value),
  });
};

export const removeElement = (design, id) => {
  const found = findElement(design, id);
  if (!found) return design;

  return mapPage(design, found.pageIndex, (elements) =>
    elements.filter((element) => element.id !== id)
  );
};

export const duplicateElementById = (design, id) => {
  const found = findElement(design, id);
  if (!found) return { design, id: null };

  const copy = {
    ...JSON.parse(JSON.stringify(found.element)),
    id: nextElementId(design, found.element.type),
  };

  return {
    design: mapPage(design, found.pageIndex, (elements) => [
      ...elements.slice(0, found.index + 1),
      copy,
      ...elements.slice(found.index + 1),
    ]),
    id: copy.id,
  };
};

/**
 * Move one step up (-1) or down (+1). At a page edge the element crosses into
 * the neighbouring page instead of refusing to move.
 */
export const moveElement = (design, id, delta) => {
  const found = findElement(design, id);
  if (!found || !delta) return design;

  const { pageIndex, index } = found;
  const elements = design.pages[pageIndex].elements || [];
  const target = index + delta;

  if (target >= 0 && target < elements.length) {
    const reordered = [...elements];
    reordered.splice(target, 0, reordered.splice(index, 1)[0]);
    return mapPage(design, pageIndex, () => reordered);
  }

  const nextPageIndex = pageIndex + delta;
  if (nextPageIndex < 0 || nextPageIndex >= design.pages.length) return design;

  const moved = elements[index];
  const pages = design.pages.map((page, pageAt) => {
    if (pageAt === pageIndex) {
      return { ...page, elements: elements.filter((item) => item.id !== id) };
    }
    if (pageAt === nextPageIndex) {
      const into = page.elements || [];
      return {
        ...page,
        elements: delta > 0 ? [moved, ...into] : [...into, moved],
      };
    }
    return page;
  });

  return { ...design, pages };
};

/** Drop an element at an explicit slot — what the canvas's drag-and-drop uses. */
export const moveElementTo = (design, id, pageNumber, position) => {
  const found = findElement(design, id);
  if (!found) return design;

  const targetPage = Math.min(
    Math.max(1, pageNumber || 1),
    design.pages.length
  );

  const without = mapPage(design, found.pageIndex, (elements) =>
    elements.filter((element) => element.id !== id)
  );

  const pageIndex = targetPage - 1;
  const elements = without.pages[pageIndex].elements || [];
  // Removing the element first shifts every later slot on that page up by one.
  const shift =
    found.pageIndex === pageIndex && found.index < position ? -1 : 0;
  const at = Math.min(Math.max(0, position + shift), elements.length);

  return mapPage(without, pageIndex, (current) => [
    ...current.slice(0, at),
    found.element,
    ...current.slice(at),
  ]);
};

export const addPage = (design) => ({
  ...design,
  pages: renumber([...design.pages, { pageNumber: 0, elements: [] }]),
});

/** The last page is never removed — a consent always has one page. */
export const removePage = (design, pageNumber) => {
  if (design.pages.length < 2) return design;

  return {
    ...design,
    pages: renumber(
      design.pages.filter((page) => page.pageNumber !== pageNumber)
    ),
  };
};

export const textElements = (design) =>
  (design?.pages || []).flatMap((page) =>
    (page.elements || []).flatMap((element) => {
      const paths = elementDef(element.type)?.localizedPaths || [];
      return paths.length
        ? [{ element, pageNumber: page.pageNumber, paths }]
        : [];
    })
  );

/** Items with no text in `lang` — the Hindi tab's "still to write" list. */
export const missingTranslations = (design, lang) =>
  textElements(design).flatMap(({ element, pageNumber, paths }) =>
    paths
      .filter((path) => {
        const other = localized(element[path]);
        return Boolean(other) && !rawLocalized(element[path], lang).trim();
      })
      .map((path) => ({ elementId: element.id, path, pageNumber }))
  );

export const dynamicElements = (design) =>
  (design?.pages || []).flatMap((page) =>
    (page.elements || []).flatMap((element) => {
      const def = elementDef(element.type);
      if (def?.dynamic) return [{ element, pageNumber: page.pageNumber }];
      if (def?.fieldList) return [{ element, pageNumber: page.pageNumber }];
      return [];
    })
  );

export const fieldMappingsOf = (design) =>
  dynamicElements(design).flatMap(({ element, pageNumber }) => {
    const def = elementDef(element.type);
    const base = {
      elementId: element.id,
      elementType: element.type,
      pageNumber,
      label: localized(element.label) || localized(element.content),
    };

    if (def?.fieldList) {
      return (element.props?.fields || []).map((field) => ({
        ...base,
        field,
        placeholder: placeholderOf(field),
        required: false,
      }));
    }

    return element.field
      ? [
          {
            ...base,
            field: element.field,
            placeholder: placeholderOf(element.field),
            required: Boolean(element.props?.required),
            format: element.props?.format || "",
            fallback: element.props?.fallback || "",
          },
        ]
      : [];
  });

export const unusedFields = (design) => {
  const used = new Set(fieldMappingsOf(design).map((item) => item.field));
  return CONSENT_FIELDS.filter((field) => !used.has(field.key));
};

export const previewSheets = (design) => {
  const sheets = [];

  for (const page of design?.pages || []) {
    let current = [];

    for (const element of page.elements || []) {
      if (element.type === "pageBreak") {
        sheets.push(current);
        current = [];
        continue;
      }
      if (element.layout?.pageBreakBefore && current.length) {
        sheets.push(current);
        current = [];
      }
      current.push(element);
      if (element.layout?.pageBreakAfter) {
        sheets.push(current);
        current = [];
      }
    }

    sheets.push(current);
  }

  const printable = sheets.filter((sheet) => sheet.length > 0);
  // An empty design still prints one blank sheet rather than nothing at all.
  return printable.length ? printable : [[]];
};

export const elementSummary = (element, lang = DEFAULT_LANGUAGE) => {
  const label =
    localized(element.label, lang) || localized(element.content, lang);
  const typeLabel = elementTypeLabel(element.type);

  if (!label) return typeLabel;
  const short = label.length > 46 ? `${label.slice(0, 45)}…` : label;
  return `${typeLabel} — ${short}`;
};

export const starterDesign = ({ displayName, name, hindiName } = {}) => {
  const heading = (displayName || name || "CONSENT FORM").toUpperCase();

  let design = emptyDesign();
  for (const type of [
    "clinicLogo",
    "heading",
    "patientInfo",
    "diagnosis",
    "treatment",
    "consentDeclaration",
    "signatureBlock",
  ]) {
    design = addElement(design, { type }).design;
  }

  design = setElementText(design, "heading-1", "content", "en", heading);
  if (hindiName) {
    design = setElementText(design, "heading-1", "content", "hi", hindiName);
  }

  return design;
};

export const validateBasicInfo = ({ name, type } = {}) => {
  const errors = {};
  if (!String(name || "").trim()) errors.name = "Consent name is required";
  if (!String(type || "").trim()) errors.type = "Consent type is required";
  return errors;
};
